import classnames from 'classnames/bind'
import { isNull } from 'lodash-es'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

// Libs
import { isLiveMallClub } from '@/constants/systemType'

// Components

// Constants

// Styles
import styles from './style.module.scss'

// Mock Data

const cx = classnames.bind(styles)

const MIN_QTY = isLiveMallClub ? 1 : 0

interface CounterProps {
  onChange: (amount: number) => void
  maximum: number
  defaultValue?: number
  hidden?: boolean
  resetValue?: number
  showText?: boolean
  maxInputWidth?: string
  className?: string
  isOutOfStock?: boolean
  shouldDefaultOnChange?: boolean
  labelClassName?: string
  isFullScreen?: boolean
  noPadding?: boolean
}

interface IProductAmountDescript {
  className: string
  text: string
}

const Counter = ({
  onChange,
  maximum,
  defaultValue = 0,
  hidden = true,
  resetValue,
  showText = true,
  maxInputWidth,
  className,
  isOutOfStock,
  shouldDefaultOnChange = true,
  labelClassName,
  isFullScreen,
  noPadding = false,
}: CounterProps) => {
  const { t } = useTranslation()

  const [itemNumber, setItemNumber] = useState(defaultValue)
  const [isQuantityChanged, setIsQuantityChanged] = useState(false)

  const isDisable = maximum === 0

  const getProductAmountDescription = (): IProductAmountDescript | null => {
    let result = null

    if (!defaultValue) {
      if (maximum <= 0 || isOutOfStock) {
        result = Object.assign({}, result, { className: 'red', text: t('Product_Status_OutStock') })
      } else if (maximum > 0 && maximum <= 5) {
        result = Object.assign({}, result, { className: 'red', text: t('Product_Status_LessStock') })
      } else {
        result = Object.assign({}, result, { className: isLiveMallClub ? 'pink' : 'green', text: t('Product_Status_InStock') })
      }
    }

    return result
  }

  useEffect(() => {
    if (!!resetValue && resetValue > 0) {
      setItemNumber(resetValue)
    }
  }, [resetValue])

  useEffect(() => {
    if (!defaultValue) {
      if (maximum <= 0 || isOutOfStock) {
        setItemNumber(0)
      } else if (maximum > 0 && maximum <= 5) {
        setItemNumber(1)
      } else {
        setItemNumber(1)
      }
    }
  }, [maximum])

  // Edge cases
  useEffect(() => {
    if (!shouldDefaultOnChange && !isQuantityChanged) {
      setIsQuantityChanged(true)
    } else {
      onChange(itemNumber)
    }
  }, [itemNumber])

  const handlePlus = (e: any) => {
    e.stopPropagation()

    if (itemNumber < maximum) {
      setItemNumber(itemNumber + 1)
    }
  }

  const handleMinus = (e: any) => {
    e.stopPropagation()

    if (itemNumber > MIN_QTY) {
      setItemNumber(itemNumber - 1)
    }
  }

  const handleSetNumber = (value: string) => {
    const newItemNumber = Number(value)

    if (!newItemNumber && newItemNumber !== 0) {
      setItemNumber(itemNumber)
    } else if (newItemNumber > maximum) {
      setItemNumber(maximum)
    } else if (newItemNumber < 0) {
      setItemNumber(0)
    } else {
      setItemNumber(newItemNumber)
    }
  }

  return (
    <div className={cx('counter', className)}>
      <div className={cx('counter__wrapper')}>
        {showText && <div className={cx('counter-name', labelClassName)}>{t('Common_Amount')}</div>}

        <div className={cx('counter__row', { isFullScreen, isLiveMallClub, noPadding })}>
          <div className={cx('counter-value', itemNumber <= MIN_QTY && 'disable', { isLiveMallClub })} onClick={(e) => handleMinus(e)}>
            <div className={cx('counter-value-text')}>-</div>
          </div>
          <input
            className={cx('counter-input', isDisable && 'disable', { isLiveMallClub })}
            type='text'
            value={itemNumber}
            onChange={(e) => handleSetNumber(e.target.value)}
            disabled={isDisable}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: maxInputWidth ? maxInputWidth : '100px' }}
          />
          <div className={cx('counter-value', itemNumber >= maximum && 'disable', { isLiveMallClub })} onClick={(e) => handlePlus(e)}>
            <div className={cx('counter-value-text')}>+</div>
          </div>

          {(!hidden || isOutOfStock) && !isNull(getProductAmountDescription()) && (
            <div className={cx('counter-description', getProductAmountDescription()?.className, { isLiveMallClub })}>
              {getProductAmountDescription()?.text}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Counter
